import { useState } from "react";
import Message from "../../components/ui/Message";

// Cambios de precio en caja (Mi empresa): rebaja máxima sin autorización, cuándo se pide el PIN de
// supervisor y el PIN. El API aplica estas reglas al guardar la orden.
export const PRICE_APPROVAL_OPTIONS = [
    { value: 'NONE', label: 'No pedir autorización', help: 'La rebaja hasta el límite es libre; lo que pase del límite o quede bajo costo no se permite.' },
    { value: 'OVER_LIMIT', label: 'Solo sobre el límite', help: 'La rebaja hasta el límite es libre; si pasa del límite o queda bajo costo, la autoriza un supervisor con el PIN.' },
    { value: 'ALWAYS', label: 'En todo cambio de precio', help: 'Cualquier cambio de precio en caja lo autoriza un supervisor con el PIN.' },
];

interface Props {
    merchant: any;
    configMerchant: (merchant: any) => Promise<string>;
}

export const PriceChangeSettings = ({ merchant, configMerchant }: Props) => {
    const initial = {
        mode: merchant?.priceChangeApproval ?? 'NONE',
        maxPct: String(Number(merchant?.priceChangeMaxPct ?? 20)),
        hasPin: !!merchant?.hasPriceChangePin,
    };
    const [saved, setSaved] = useState(initial);
    const [mode, setMode] = useState(initial.mode);
    const [maxPct, setMaxPct] = useState(initial.maxPct);
    const [pin, setPin] = useState('');
    const [clearPin, setClearPin] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const hasPin = clearPin ? false : (saved.hasPin || pin.length > 0);
    const hasChanges = mode !== saved.mode || Number(maxPct) !== Number(saved.maxPct) || pin.length > 0 || clearPin;
    const pctValid = maxPct !== '' && Number(maxPct) >= 0 && Number(maxPct) <= 100;
    const pinValid = pin === '' || /^\d{4,8}$/.test(pin);
    const needsPin = mode !== 'NONE' && !hasPin;

    const handleSave = async () => {
        if (!pctValid) return setErrorMessage('La rebaja máxima debe estar entre 0 y 100 %.');
        if (!pinValid) return setErrorMessage('El PIN debe tener de 4 a 8 dígitos.');
        if (needsPin) return setErrorMessage('Configure el PIN de supervisor para pedir autorización.');

        setIsSaving(true);
        const resp = await configMerchant({
            id: merchant.id,
            priceChangeApproval: mode,
            priceChangeMaxPct: Number(maxPct),
            ...(pin ? { priceChangePin: pin } : {}),
            ...(clearPin ? { clearPriceChangePin: true } : {}),
        });
        setIsSaving(false);

        if (resp === 'success') {
            setSaved({ mode, maxPct: String(Number(maxPct)), hasPin });
            setPin('');
            setClearPin(false);
            setSuccessMessage('Configuración de cambios de precio actualizada');
        } else {
            setErrorMessage(resp);
        }
    };

    const handleReset = () => {
        setMode(saved.mode);
        setMaxPct(saved.maxPct);
        setPin('');
        setClearPin(false);
    };

    return (
        <div className="card border-0 shadow-sm">
            <div className="card-body">
                <h6 className="mb-1">Cambios de precio en caja</h6>
                <p className="text-muted small mb-3">Aplica a los productos con "Precio editable". La rebaja se registra como descuento en la factura.</p>

                <div className="border rounded p-3 mb-3">
                    <label htmlFor="priceMaxPct" className="fw-medium form-label mb-1">Rebaja máxima sin autorización</label>
                    <div className="input-group" style={{ maxWidth: 180 }}>
                        <input
                            id="priceMaxPct"
                            type="number"
                            className={`form-control${pctValid ? '' : ' is-invalid'}`}
                            min={0}
                            max={100}
                            step="0.5"
                            value={maxPct}
                            onChange={(e) => setMaxPct(e.target.value)}
                        />
                        <span className="input-group-text">%</span>
                    </div>
                    <div className="text-muted small mt-1">Porcentaje sobre el precio vigente del producto (el de oferta, si tiene).</div>
                </div>

                <div className="border rounded p-3 mb-3">
                    <div className="fw-medium mb-2">Autorización de supervisor</div>
                    {PRICE_APPROVAL_OPTIONS.map(option => (
                        <div className="form-check mb-2" key={option.value}>
                            <input
                                id={`priceApproval-${option.value}`}
                                className="form-check-input"
                                type="radio"
                                name="priceApproval"
                                value={option.value}
                                checked={mode === option.value}
                                onChange={() => setMode(option.value)}
                            />
                            <label className="form-check-label" htmlFor={`priceApproval-${option.value}`}>
                                <div>{option.label}</div>
                                <div className="text-muted small">{option.help}</div>
                            </label>
                        </div>
                    ))}
                </div>

                <div className="border rounded p-3 mb-3">
                    <label htmlFor="pricePin" className="fw-medium form-label mb-1">PIN de supervisor</label>
                    <div className="text-muted small mb-2">
                        {saved.hasPin && !clearPin
                            ? <><i className="bi bi-check-circle text-success me-1" />Hay un PIN configurado. Escriba uno nuevo para cambiarlo.</>
                            : 'Sin PIN configurado.'}
                    </div>
                    <input
                        id="pricePin"
                        type="password"
                        inputMode="numeric"
                        autoComplete="new-password"
                        className={`form-control${pinValid ? '' : ' is-invalid'}`}
                        style={{ maxWidth: 180 }}
                        placeholder="4 a 8 dígitos"
                        maxLength={8}
                        value={pin}
                        disabled={clearPin}
                        onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                    />
                    {saved.hasPin && (
                        <div className="form-check mt-2">
                            <input id="clearPricePin" className="form-check-input" type="checkbox" checked={clearPin}
                                onChange={(e) => { setClearPin(e.target.checked); setPin(''); }} />
                            <label className="form-check-label small" htmlFor="clearPricePin">Quitar el PIN</label>
                        </div>
                    )}
                    {needsPin && <div className="text-danger small mt-2">Este modo necesita un PIN de supervisor.</div>}
                </div>

                <div className="d-flex justify-content-end align-items-center gap-2 pt-1">
                    {hasChanges && <span className="text-muted small me-auto"><i className="bi bi-info-circle me-1" />Tiene cambios sin guardar.</span>}
                    <button type="button" className="btn btn-outline-secondary" onClick={handleReset} disabled={!hasChanges || isSaving}>
                        Descartar
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSave} disabled={!hasChanges || isSaving}>
                        {isSaving
                            ? <><span className="spinner-border spinner-border-sm me-1" /> Guardando...</>
                            : <><i className="bi bi-check-lg me-1" /> Guardar cambios</>}
                    </button>
                </div>
            </div>

            {errorMessage &&
                <Message type='error' message={errorMessage} closeDialog={() => setErrorMessage('')} handleOk={() => setErrorMessage('')} backgroundColor='rgba(0,0,0,0.4)' />}
            {successMessage &&
                <Message type='success' message={successMessage} title='Exitoso' closeDialog={() => setSuccessMessage('')} handleOk={() => setSuccessMessage('')} backgroundColor='rgba(0,0,0,0.4)' />}
        </div>
    );
};
